import React from "react";
import { wrap } from "comlink";
import getEnv from "env";
import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, msalConfig } from "app/auth/useAuth";

const REACT_APP_ITEMS_PER_PAGE_CSV = getEnv("REACT_APP_ITEMS_PER_PAGE_CSV");
const REACT_APP_VIATRACKING_API_URL = getEnv("REACT_APP_VIATRACKING_API_URL");
const REACT_APP_VIATRACKING_KEY_DECRYPT = getEnv(
  "REACT_APP_VIATRACKING_KEY_DECRYPT"
);
const REACT_APP_ASAP_BASE_URL = getEnv("REACT_APP_ASAP_BASE_URL");
const REACT_APP_ASAP_LML_BASE_URL = getEnv("REACT_APP_ASAP_LML_BASE_URL");
const REACT_APP_ASAP_MINIHUB_BASE_URL = getEnv(
  "REACT_APP_ASAP_MINIHUB_BASE_URL"
);
const REACT_APP_ASAP_FRACIONADOS_BASE_URL = getEnv(
  "REACT_APP_ASAP_FRACIONADOS_BASE_URL"
);
const REACT_APP_REDIRECT_URL = getEnv("REACT_APP_REDIRECT_URL");

const defaultCsvWorker = new Worker(
  new URL(`app/workers/default.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const matrizCsvWorker = new Worker(
  new URL(`app/workers/matriz.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const insumosCsvWorker = new Worker(
  new URL(`app/workers/insumos.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const acareacoesCsvWorker = new Worker(
  new URL(`app/workers/acareacoes.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const listaPedidosCsvWorker = new Worker(
  new URL(`app/workers/listaPedidos.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const listaRoteirosCsvWorker = new Worker(
  new URL(`app/workers/listaRoteiros.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const listaPedidosLMLCsvWorker = new Worker(
  new URL(`app/workers/listaPedidosLML.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const listaRoteirosLMLCsvWorker = new Worker(
  new URL(`app/workers/listaRoteirosLML.csvExport.worker.ts`, import.meta.url),
  { type: "module" }
);

const listaPedidosMiniHubCsvWorker = new Worker(
  new URL(
    `app/workers/listaPedidosMiniHub.csvExport.worker.ts`,
    import.meta.url
  ),
  { type: "module" }
);

const listaRoteirosMiniHubCsvWorker = new Worker(
  new URL(
    `app/workers/listaRoteirosMiniHub.csvExport.worker.ts`,
    import.meta.url
  ),
  { type: "module" }
);

const listaPedidosFracionadosCsvWorker = new Worker(
  new URL(
    `app/workers/listaPedidosFracionados.csvExport.worker.ts`,
    import.meta.url
  ),
  { type: "module" }
);

const relatorioComprovantesCsvWorker = new Worker(
  new URL(
    `app/workers/relatorioComprovantes.csvExport.worker.ts`,
    import.meta.url
  ),
  { type: "module" }
);

const initialStateError = {
  isError: false,
  errorMessage: "",
};

export const useExportCsv = (params: any) => {
  const token = sessionStorage.getItem("AccessToken")
    ? sessionStorage.getItem("AccessToken")
    : sessionStorage?.AccessToken;
  const idSession = sessionStorage.getItem("SessionID")
    ? sessionStorage.getItem("SessionID")
    : sessionStorage?.SessionID;
  const msToken = sessionStorage.getItem("msToken")
    ? sessionStorage.getItem("msToken")
    : sessionStorage?.msToken;

  const { activeExport, view } = params;

  const { defaultCsvExport } = wrap(defaultCsvWorker);
  const { matrizCsv } = wrap(matrizCsvWorker);
  const { insumosCsv } = wrap(insumosCsvWorker);
  const { acareacoesCsv } = wrap(acareacoesCsvWorker);
  const { listaPedidosCsv } = wrap(listaPedidosCsvWorker);
  const { listaRoteirosCsv } = wrap(listaRoteirosCsvWorker);
  const { listaPedidosLMLCsv } = wrap(listaPedidosLMLCsvWorker);
  const { listaRoteirosLMLCsv } = wrap(listaRoteirosLMLCsvWorker);
  const { listaPedidosMiniHubCsv } = wrap(listaPedidosMiniHubCsvWorker);
  const { listaRoteirosMiniHubCsv } = wrap(listaRoteirosMiniHubCsvWorker);
  const { listaPedidosFracionadosCsv } = wrap(listaPedidosFracionadosCsvWorker);
  const { relatorioComprovantesCsv } = wrap(relatorioComprovantesCsvWorker);

  const [progress, setProgress] = React.useState<number>(0);
  const [buffer, setBuffer] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(100);
  const [activeForDownload, setActiveForDownload] = React.useState(false);
  const [csvData, setCsvData] = React.useState<any>("");
  const [error, setError] = React.useState(initialStateError);
  const [cancel, setCancel] = React.useState(false);

  // Função para renovar o token usando MSAL
  const renewToken = async () => {
    try {
      const msalInstance = new PublicClientApplication(msalConfig);
      const account = msalInstance.getAllAccounts()[0];

      if (!account) {
        throw new Error("Nenhuma conta encontrada");
      }

      const accessTokenRequest = {
        ...loginRequest,
        account: account,
        prompt: "none",
        responseType: "id_token token",
      };

      const response = await msalInstance.acquireTokenSilent(
        accessTokenRequest
      );

      // Atualizar o token no sessionStorage
      sessionStorage.setItem("msToken", response.accessToken);

      return response.accessToken;
    } catch (error) {
      console.error("Erro ao renovar o token:", error);
      throw error;
    }
  };

  // Configurar listeners para mensagens dos Workers
  React.useEffect(() => {
    // Função para lidar com mensagens de solicitação de token
    const handleWorkerMessages = async (event) => {
      if (event.data && event.data.type === "TOKEN_RENEWAL_REQUEST") {
        console.log("Recebida solicitação de renovação de token do Worker");
        try {
          const newToken = await renewToken();
          console.log(
            "Token renovado com sucesso, enviando de volta para o Worker"
          );

          // Enviar o novo token de volta para o Worker
          event.target.postMessage({
            type: "TOKEN_RENEWAL_RESPONSE",
            token: newToken,
          });
        } catch (error) {
          console.error("Erro ao renovar o token:", error);
          event.target.postMessage({
            type: "TOKEN_RENEWAL_ERROR",
            error: error?.message || "Erro ao renovar o token",
          });
        }
      } else if (event.data && event.data.type === "AUTH_ERROR") {
        console.error("Erro de autenticação no Worker:", event.data);
        setError({
          isError: true,
          errorMessage:
            "Erro de autenticação. Por favor, faça login novamente.",
        });
      } else if (event.data && event.data.type === "TOKEN_RENEWAL_ERROR") {
        console.error("Erro na renovação do token no Worker:", event.data);
        setError({
          isError: true,
          errorMessage: "Erro ao renovar o token. Por favor, tente novamente.",
        });
      }
    };

    // Adicionar listeners para cada Worker
    defaultCsvWorker.addEventListener("message", handleWorkerMessages);
    matrizCsvWorker.addEventListener("message", handleWorkerMessages);
    insumosCsvWorker.addEventListener("message", handleWorkerMessages);
    acareacoesCsvWorker.addEventListener("message", handleWorkerMessages);
    listaPedidosCsvWorker.addEventListener("message", handleWorkerMessages);
    listaRoteirosCsvWorker.addEventListener("message", handleWorkerMessages);
    listaPedidosLMLCsvWorker.addEventListener("message", handleWorkerMessages);
    listaRoteirosLMLCsvWorker.addEventListener("message", handleWorkerMessages);
    listaPedidosMiniHubCsvWorker.addEventListener(
      "message",
      handleWorkerMessages
    );
    listaRoteirosMiniHubCsvWorker.addEventListener(
      "message",
      handleWorkerMessages
    );
    listaPedidosFracionadosCsvWorker.addEventListener(
      "message",
      handleWorkerMessages
    );
    relatorioComprovantesCsvWorker.addEventListener(
      "message",
      handleWorkerMessages
    );

    // Limpar listeners quando o componente for desmontado
    return () => {
      defaultCsvWorker.removeEventListener("message", handleWorkerMessages);
      matrizCsvWorker.removeEventListener("message", handleWorkerMessages);
      insumosCsvWorker.removeEventListener("message", handleWorkerMessages);
      acareacoesCsvWorker.removeEventListener("message", handleWorkerMessages);
      listaPedidosCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaRoteirosCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaPedidosLMLCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaRoteirosLMLCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaPedidosMiniHubCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaRoteirosMiniHubCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      listaPedidosFracionadosCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
      relatorioComprovantesCsvWorker.removeEventListener(
        "message",
        handleWorkerMessages
      );
    };
  }, []);

  React.useEffect(() => {
    if (!activeExport) return;
    switch (view) {
      case "matriz":
        matrizCsv(
          params,
          token,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_VIATRACKING_API_URL,
          REACT_APP_VIATRACKING_KEY_DECRYPT
        );
        break;
      case "insumos":
        insumosCsv(
          params,
          token,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_VIATRACKING_API_URL,
          REACT_APP_VIATRACKING_KEY_DECRYPT
        );
        break;
      case "acareacoes":
        acareacoesCsv(
          params,
          token,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_VIATRACKING_API_URL,
          REACT_APP_VIATRACKING_KEY_DECRYPT
        );
        break;
      case "listaPedidos":
        listaPedidosCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaRoteiros":
        listaRoteirosCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaPedidosLML":
        listaPedidosLMLCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_LML_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaRoteirosLML":
        listaRoteirosLMLCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_LML_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaPedidosMiniHub":
        listaPedidosMiniHubCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_MINIHUB_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaRoteirosMiniHub":
        listaRoteirosMiniHubCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_MINIHUB_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "listaPedidosFracionados":
        listaPedidosFracionadosCsv(
          params,
          msToken,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_ASAP_FRACIONADOS_BASE_URL,
          REACT_APP_REDIRECT_URL
        );
        break;
      case "relatorioComprovantes":
        relatorioComprovantesCsv(
          params,
          token,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_VIATRACKING_API_URL,
          REACT_APP_VIATRACKING_KEY_DECRYPT
        );
        break;
      default:
        defaultCsvExport(
          params,
          token,
          idSession,
          REACT_APP_ITEMS_PER_PAGE_CSV,
          REACT_APP_VIATRACKING_API_URL,
          REACT_APP_VIATRACKING_KEY_DECRYPT
        );
        break;
    }
  }, [activeExport]);

  React.useEffect(() => {
    if (cancel) {
      try {
        switch (view) {
          case "matriz":
            matrizCsvWorker.postMessage("Stop");
            break;
          case "insumos":
            insumosCsvWorker.postMessage("Stop");
            break;
          case "acareacoes":
            acareacoesCsvWorker.postMessage("Stop");
            break;
          case "listaPedidos":
            listaPedidosCsvWorker.postMessage("Stop");
            break;
          case "listaRoteiros":
            listaRoteirosCsvWorker.postMessage("Stop");
            break;
          case "listaPedidosLML":
            listaPedidosLMLCsvWorker.postMessage("Stop");
            break;
          case "listaRoteirosLML":
            listaRoteirosLMLCsvWorker.postMessage("Stop");
            break;
          case "listaPedidosMiniHub":
            listaPedidosMiniHubCsvWorker.postMessage("Stop");
            break;
          case "listaRoteirosMiniHub":
            listaRoteirosMiniHubCsvWorker.postMessage("Stop");
            break;
          case "listaPedidosFracionados":
            listaPedidosFracionadosCsvWorker.postMessage("Stop");
            break;
          case "relatorioComprovantes":
            relatorioComprovantesCsvWorker.postMessage("Stop");
            break;
          default:
            defaultCsvWorker.postMessage("Stop");
            break;
        }
      } catch (err) {
        setCancel(false);
        console.log("Request not canceled", err);
      }
      setError(initialStateError);
      setTotalPages(100);
      setProgress(0);
      setActiveForDownload(false);
    }
    return () => {
      setError(initialStateError);
      setTotalPages(100);
      setProgress(0);
      setActiveForDownload(false);
    };
  }, [cancel]);

  const updateProgress = (page, buff, allPages, csvString = "") => {
    if (page === 1) {
      setProgress(page);
      setTotalPages(allPages);
    } else {
      setProgress(page);
      setBuffer(buff);
    }
    if (page === allPages) {
      setActiveForDownload(true);
      setCsvData(csvString);
    }
  };

  defaultCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (e.data.hasOwnProperty("page")) {
        updateProgress(
          e.data.page,
          e.data.buff,
          e.data.allPages,
          e.data?.csvString
        );
      }
    }
  };

  matrizCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "matriz") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        matrizCsvWorker.postMessage("Stop");
      }
    }
  };

  insumosCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (e.data.hasOwnProperty("page")) {
        updateProgress(
          e.data.page,
          e.data.buff,
          e.data.allPages,
          e.data?.csvString
        );
      }
    }
  };

  acareacoesCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (e.data.hasOwnProperty("page")) {
        updateProgress(
          e.data.page,
          e.data.buff,
          e.data.allPages,
          e.data?.csvString
        );
      }
    }
  };

  listaPedidosCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaPedidos") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaPedidosCsvWorker.postMessage("Stop");
      }
    }
  };

  listaRoteirosCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaRoteiros") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaRoteirosCsvWorker.postMessage("Stop");
      }
    }
  };

  listaPedidosLMLCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaPedidosLML") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaPedidosLMLCsvWorker.postMessage("Stop");
      }
    }
  };

  listaRoteirosLMLCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaRoteirosLML") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaRoteirosLMLCsvWorker.postMessage("Stop");
      }
    }
  };

  listaPedidosMiniHubCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaPedidosMiniHub") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaPedidosMiniHubCsvWorker.postMessage("Stop");
      }
    }
  };

  listaRoteirosMiniHubCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaRoteirosMiniHub") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaRoteirosMiniHubCsvWorker.postMessage("Stop");
      }
    }
  };

  listaPedidosFracionadosCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "listaPedidosFracionados") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        listaPedidosFracionadosCsvWorker.postMessage("Stop");
      }
    }
  };

  relatorioComprovantesCsvWorker.onmessage = (e) => {
    if (e.data.hasOwnProperty("err")) {
      setError({ isError: true, errorMessage: e.data.err });
    } else {
      if (view === "relatorioComprovantes") {
        if (e.data.hasOwnProperty("page")) {
          updateProgress(
            e.data.page,
            e.data.buff,
            e.data.allPages,
            e.data?.csvString
          );
        }
      } else {
        relatorioComprovantesCsvWorker.postMessage("Stop");
      }
    }
  };

  return {
    activeExport,
    progress,
    buffer,
    totalPages,
    activeForDownload,
    csvData,
    error,
    cancel,
    setCancel,
    setActiveForDownload,
    setError,
    setProgress,
    setTotalPages,
  };
};
